import React, {useState} from 'react';
import currency from 'currency.js';
import PricingSection from '../components/PricingSection';
import {fetchPricingPage} from '../util/pages';
import SeoTags from '../components/SeoTags';
import {withUserGetServerSideProps} from '../util/util';
import wrapper from '../store/store';
import {fetchProducts, subscribeToProduct, cancelSubscription, activateTrialSubscription} from '../util/pricing';
import {getStripe} from '../util/stripe';
import {useAuth} from '../util/auth';
import {TrialModal} from '../components/TrialModal';
import {useRouter} from 'next/router';

function PricingPage(props) {
    const [isTrialModalOpen, setIsTrialModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const {content = {}, plans = []} = props;
    const auth = useAuth();
    const router = useRouter();

    const futureSubscription = auth?.user?.futureSubscription;
    const currentSubscription = auth?.user?.subscription;
    const isTrialSubscriptionActive = auth?.user?.isTrialSubscriptionActive;

    return <>
        <SeoTags config={content.seo} />
        <TrialModal open={isTrialModalOpen} onClose={() => {
            setIsTrialModalOpen(false)
        }} onActivateTrailClick={() => {
            activateTrialSubscription().then(async (response) => {
                await auth.reloadUser();
                setIsTrialModalOpen(false);
                router.replace(`/trial/success`);
            });
        }}
        />
        <div className="container py-16">
            <PricingSection
                title={'Find **your plan**'}
                description={''}
                comparisonTable={content.comparison_table}
                plans={plans.map(it => {
                    let currencyObj = currency(it.price, {
                        fromCents: true,
                        symbol: '',
                        separator: '',
                        decimal: ','
                    });
                    
                    return {
                        featured: it.featured,
                        features: it.features,
                        id: it.id,
                        title: it.name,
                        price: currencyObj.format(),
                        currency: '€',
                        description: it.description,
                        duration: it.duration,
                        trialDuration: it.trial_duration,
                        active: currentSubscription?.product_id === it.id,
                        isTrialSubscriptionActive: isTrialSubscriptionActive,
                        hasActiveSubscription: !!currentSubscription?.product_id
                    }
                })}
                onSelect={async (plan) => {
                    if (!auth.isAuthenticated) {
                        auth.redirectToLogin({next : `/pricing`});
                        return;
                    }
                    
                    if ((auth?.user?.trialAvailable && !currentSubscription)) {
                        setIsTrialModalOpen(true);
                        return;
                    }
                    
                    if (loading) {
                        return;
                    }
                    setLoading(true);
                    
                    const subscribe = async ({id, subscription, shouldCancelSubscription}) => {
                        if (shouldCancelSubscription && subscription.id) {
                            try {
                                await cancelSubscription(subscription.id).then((response) => {
                                    if (response?.status !== 'SUCCESS') {
                                        throw new Error('Failed to cancel subscription');
                                    }
                                })
                            } catch (err) {
                                alert(err);
                            }
                        }
                        
                        try {
                            const replace = subscription?.product_id && subscription?.product_id !== id;
                            await subscribeToProduct(plan.id, {replace}).then((response) => {
                                if (!response?.stripe_public_key || !response?.session_id) {
                                    throw new Error('Not public key or session id');
                                }
                                
                                getStripe(response.stripe_public_key).then((stripe) => {
                                    stripe.redirectToCheckout({
                                        sessionId: response.session_id
                                    });
                                })
                            });
                        } catch (err) {
                            alert('Something went wrong');
                        }
                        
                        setLoading(false);
                    }
                    
                    await subscribe({
                        id: plan.id,
                        subscription: futureSubscription || currentSubscription,
                        shouldCancelSubscription: !!futureSubscription,
                    })
                }}
            />
        </div>
    </>
}

const pricingPlansFeaturesMap = {
    learner: [
        {id: "individual_workout", enabled: true, label: "Individual workout", level: 100},
        {id: "group_trainings", enabled: false, label: "Group trainings", level: 100},
        {id: "personal_coach", enabled: false, label: "Personal coach", level: 100},

        {id: "cases_summaries", enabled: true, label: "Read closed cases summaries", level: 0},
        {id: "live_sessions_initiate", enabled: false, label: "Initiate live sessions for practicing tools", level: 0},
    ],
    challenger: [
        {id: "individual_workout", enabled: true, label: "Individual workout", level: 100},
        {id: "group_trainings", enabled: true, label: "Group trainings", level: 100},
        {id: "personal_coach", enabled: false, label: "Personal coach", level: 100},

        {id: "cases_summaries", enabled: true, label: "Read closed cases summaries", level: 0},
        {id: "live_sessions_initiate", enabled: true, label: "Initiate live sessions for practicing tools", level: 0},
    ],
    achiever: [
        {id: "individual_workout", enabled: true, label: "Individual workout", level: 100},
        {id: "group_trainings", enabled: true, label: "Group trainings", level: 100},
        {id: "personal_coach", enabled: true, label: "Personal coach", level: 100},

        {id: "cases_summaries", enabled: true, label: "Read closed cases summaries", level: 0},
        {id: "live_sessions_initiate", enabled: true, label: "Initiate live sessions for practicing tools", level: 0},
    ]
}

export const getServerSideProps = wrapper.getServerSideProps(withUserGetServerSideProps(async (ctx) => {
    const [content, products] = await Promise.all([
        fetchPricingPage(),
        fetchProducts()
    ]);

    return { props: {content, plans: products.map(p => {
        return {
            ...p,
            features: [
                ...p.features,
                ...(pricingPlansFeaturesMap[p.id] || [])
            ]
        }
    })} }
}));


export default PricingPage;
